import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";

/**
 * Mealplan's OWN auth shell — deliberately NOT the shared `AuthLayout` (the
 * split brand-panel + centered glass `AuthCard` that every clone inherited and
 * which therefore read as "the same template").
 *
 * Where listto chose a bare editorial column, Mealplan leans into its kitchen
 * soul: the auth screen reads like a **warm recipe / menu ticket**. A full-width
 * brand band sits on a soft accent-tinted "menu header" surface (logo chip +
 * appetizing weekly-plan motif + eyebrow chip + headline), then a **dotted
 * menu-ticket rule** separates it from the form, which falls beneath as a plain
 * field list directly on the ambient background — no glass card, no split.
 *
 *   ╭──────────────────────────────╮  ← brand band on bg-accent/8 "menu header"
 *   │ ◳ mealplan          · chip ·  │     with a faint plated-week motif at right
 *   │ Big appetizing headline.     │
 *   │ Supporting line.             │
 *   ╰╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╴╮  ← dotted ticket rule (no card border)
 *   │  email   [____________]      │  ← form full-bleed on the ambient bg
 *   │  pass    [____________]      │
 *   │  [ Sign in ]                 │
 *   ╰──────────────────────────────╯
 *
 * So two products opened side by side do not look like the same shell: listto is
 * a flat editorial column, the generic kit was a centered glass card in a split,
 * and Mealplan is a menu-ticket with a tinted header band and a perforated rule.
 * The shell stays agnostic about the *form* — screens pour their fields in as
 * `children` and share the primitives (buttons, fields, SocialAuthButtons,
 * disabled-until-ready) without sharing the silhouette.
 *
 * ── SCROLL CONTRACT (#13, same fix as the kit) ───────────────────────────────
 * `AppBackground` is `position: fixed; inset: 0; overflow: hidden` and clips its
 * children, so it is used here ONLY as a decorative backdrop **sibling** — never
 * as the parent that scrolls. The content lives in its own scroll container.
 *
 * Mealplan's `html, body` are `overflow: hidden; height: 100%` (to keep stray
 * portals from spawning body scrollbars over the fixed `AppBackground`). That
 * means the WINDOW cannot scroll — so a plain `min-h-[100dvh]` wrapper would
 * just grow past the viewport and clip the overflow with nowhere to scroll. The
 * fix (per the kit, the `html,body: overflow:hidden` branch): **cap the scroll
 * container to the viewport** with `h-[100dvh] overflow-y-auto`, so IT becomes
 * the scroller. A `min-h-full` inner column centers the ticket when it fits and
 * grows (forcing the container to scroll) when a tall form (register) or a short
 * landscape viewport does not. Safe-areas are honoured for notched devices/PWAs.
 */

interface MealplanAuthSceneProps {
  /** Eyebrow chip (e.g. "Welcome back"). Optional. */
  eyebrow?: string;
  /** Page headline — the appetizing promise for this screen. Optional for bare loading states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The scene headline IS the page h1 for
   * Mealplan's auth (there is no card title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Width of the ticket. "narrow" for login/forgot/reset/verify, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), rendered full-bleed below the menu rule. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function MealplanAuthScene(props: MealplanAuthSceneProps) {
  const headingClass = "text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl";

  return (
    <>
      {/* Decorative fixed backdrop sibling — NEVER the scroll parent. */}
      <AppBackground />

      {/* The real scroll container: grows past the viewport and scrolls when the
          form does not fit (portrait, or short landscape). */}
      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-4 sm:px-6"
        style={{
          "padding-top": "max(1.5rem, env(safe-area-inset-top))",
          "padding-bottom": "max(1.5rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1rem, env(safe-area-inset-left))",
          "padding-right": "max(1rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-7"
          classList={{
            "max-w-sm": (props.width ?? "narrow") === "narrow",
            "max-w-md": props.width === "wide",
          }}
        >
          {/* The "menu ticket": tinted header band + perforated rule + form. */}
          <div class="overflow-hidden rounded-3xl bg-card/30 ring-1 ring-border/60 backdrop-blur-sm">
            {/* Brand band — the warm "menu header". */}
            <header class="relative overflow-hidden bg-accent/[0.08] px-6 pt-7 pb-6 sm:px-8">
              <TicketMotif />

              <div class="relative z-10 flex flex-col gap-5">
                <div class="flex items-center justify-between gap-3">
                  <Link
                    to="/"
                    class="group flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    aria-label="Mealplan"
                  >
                    <span class="grid h-11 w-11 place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95">
                      <svg
                        viewBox="0 0 32 32"
                        fill="none"
                        class="h-6 w-6"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        {/* Plate */}
                        <circle cx="16" cy="17" r="8.4" stroke-width="1.8" />
                        <circle cx="16" cy="17" r="4" stroke-width="1.3" opacity="0.55" />
                        {/* Fork */}
                        <g stroke-width="1.7" stroke-linecap="round">
                          <path d="M8.5 9.5v3.4" />
                          <path d="M11 9.5v3.4" />
                          <path d="M9.75 12.9V25" />
                        </g>
                        {/* Spoon */}
                        <path
                          d="M22.5 9.6c1.7 0 2.7 1.4 2.7 3 0 1.4-.9 2.4-2.2 2.6M22.9 15.4 22.5 25"
                          stroke-width="1.7"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                    <span class="text-lg font-bold tracking-tight text-foreground">mealplan</span>
                  </Link>
                  <Show when={props.eyebrow}>
                    <span class="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground shadow-sm ring-1 ring-black/5">
                      {props.eyebrow}
                    </span>
                  </Show>
                </div>

                <Show when={props.headline}>
                  <div class="space-y-2">
                    <Show
                      when={(props.headingLevel ?? "h1") === "h2"}
                      fallback={<h1 class={headingClass}>{props.headline}</h1>}
                    >
                      <h2 class={headingClass}>{props.headline}</h2>
                    </Show>
                    <Show when={props.tagline}>
                      <p class="text-pretty text-sm leading-relaxed text-muted-foreground sm:text-base">
                        {props.tagline}
                      </p>
                    </Show>
                  </div>
                </Show>
              </div>
            </header>

            {/* Perforated "ticket" rule instead of a plain card border. */}
            <div class="relative h-5" aria-hidden="true">
              <div class="absolute -left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-background" />
              <div class="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-background" />
              <div class="absolute inset-x-5 top-1/2 -translate-y-1/2 border-t border-dashed border-border/70" />
            </div>

            {/* Form lives full-bleed on the ticket body (no glass card). */}
            <div class="px-6 pt-1 pb-7 sm:px-8">
              <div class="w-full">{props.children}</div>

              <Show when={props.footer}>
                <footer class="mt-7 space-y-3 text-center">{props.footer}</footer>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Faint plated-week motif in the header band — a calendar of plated days.
 * Theme-safe by construction: drawn with `currentColor` inherited from a very
 * low-opacity accent text color, so it never collides with a custom theme.
 * Purely decorative.
 */
function TicketMotif() {
  return (
    <div
      class="pointer-events-none absolute -right-6 -top-4 z-0 text-accent/[0.12]"
      aria-hidden="true"
    >
      <svg
        class="h-40 w-40"
        viewBox="0 0 48 48"
        fill="none"
        stroke="currentColor"
        stroke-width="1.2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        {/* Weekly planner card */}
        <rect x="6" y="8" width="36" height="34" rx="3.5" stroke-width="1.4" />
        <path d="M6 16h36" stroke-width="1.4" />
        <path d="M16 5v6M32 5v6" stroke-width="1.5" />
        {/* Plated day cells */}
        <circle cx="15" cy="25" r="3" />
        <circle cx="24" cy="25" r="3" />
        <circle cx="33" cy="25" r="3" />
        <circle cx="15" cy="34" r="3" />
        <circle cx="24" cy="34" r="3" />
        <circle cx="33" cy="34" r="3" />
      </svg>
    </div>
  );
}
